import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { House, SquaresFour, Buildings, Wrench, Factory, PaintBrush, Truck, Package } from "@phosphor-icons/react";
import { QrScanner, extractRoomId } from "@/components/QrScanner";
import { SecretGate } from "@/components/admin/SecretGate";
import { api, getSecret, clearSecret } from "@/lib/api";

export default function ScanPage({ mode }) {
    const navigate = useNavigate();
    const isAdmin = mode === "admin";
    const [authed, setAuthed] = useState(!isAdmin);
    const [checking, setChecking] = useState(isAdmin);
    const [manual, setManual] = useState("");

    useEffect(() => {
        if (!isAdmin) return;
        const saved = getSecret();
        if (!saved) {
            setChecking(false);
            return;
        }
        api.post("/admin/verify", { phrase: saved })
            .then(() => setAuthed(true))
            .catch(() => clearSecret())
            .finally(() => setChecking(false));
    }, [isAdmin]);

    const go = useCallback(
        (roomId) => navigate(isAdmin ? `/a/${roomId}` : `/s/${roomId}`),
        [isAdmin, navigate]
    );

    const submitManual = async (e) => {
        e.preventDefault();
        const roomId = extractRoomId(manual);
        if (roomId) {
            go(roomId);
            return;
        }
        const code = manual.trim().toUpperCase();
        if (/^[A-Z0-9]{6}$/.test(code)) {
            try {
                const res = await api.get(`/rooms/resolve/${code}`);
                go(res.data.room_id);
            } catch {
                toast.error("Kode ruangan tidak ditemukan");
            }
            return;
        }
        toast.error("Tautan/kode ruangan tidak valid");
    };

    if (checking) return null;
    if (!authed) return <SecretGate onSuccess={() => setAuthed(true)} title="Mode Admin" />;

    return (
        <div className="min-h-screen" data-testid={`scan-page-${mode}`}>
            <header className="sticky top-0 z-10 bg-white/80 backdrop-blur-xl border-b border-stone-200">
                <div className="max-w-md mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img src="/logo.png" alt="INERRE interior" className="h-9 object-contain" data-testid="scan-logo" />
                        <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground">
                            {isAdmin ? "Mode Admin" : "Mode Supervisor"}
                        </p>
                    </div>
                    <button
                        data-testid="scan-home-link"
                        onClick={() => navigate("/")}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                        title="Beranda"
                    >
                        <House size={20} weight="light" />
                    </button>
                </div>
            </header>

            <main className="max-w-md mx-auto px-6 py-8 space-y-6 fade-up">
                <div className="text-center">
                    <h1 className="font-serif text-3xl sm:text-4xl tracking-tighter font-medium">Pindai QR Ruangan</h1>
                    <p className="text-sm text-muted-foreground mt-2">
                        Arahkan kamera ke QR code yang terpasang di ruangan.
                    </p>
                </div>

                <QrScanner onScan={go} />

                <form onSubmit={submitManual} className="border border-border bg-white p-4">
                    <label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Input Manual</label>
                    <input
                        data-testid="manual-room-input"
                        value={manual}
                        onChange={(e) => setManual(e.target.value)}
                        placeholder="Tautan ruangan atau kode 6 karakter (misal: X7K2M9)"
                        className="mt-2 w-full border border-border px-3 py-2 text-sm outline-none focus:border-primary transition-colors bg-transparent"
                    />
                    <button
                        data-testid="manual-room-submit"
                        type="submit"
                        className="mt-3 w-full bg-primary text-primary-foreground py-2.5 text-sm font-medium hover:bg-[#9c4a2a] transition-colors"
                    >
                        Buka Ruangan
                    </button>
                </form>

                <button
                    data-testid="scan-browse-btn"
                    onClick={() => navigate(`/${mode}/browse`)}
                    className="w-full border border-border py-3 text-sm text-muted-foreground hover:text-foreground hover:border-primary transition-colors flex items-center justify-center gap-2"
                >
                    <Buildings size={16} weight="light" /> Lihat Semua Properti
                </button>

                {isAdmin && (
                    <button
                        data-testid="scan-produksi-btn"
                        onClick={() => navigate("/admin/produksi")}
                        className="w-full border border-border py-3 text-sm text-muted-foreground hover:text-foreground hover:border-primary transition-colors flex items-center justify-center gap-2"
                    >
                        <Factory size={16} weight="light" /> Lihat Semua Produksi
                    </button>
                )}

                {isAdmin && (
                    <button
                        data-testid="scan-finishing-btn"
                        onClick={() => navigate("/admin/finishing")}
                        className="w-full border border-border py-3 text-sm text-muted-foreground hover:text-foreground hover:border-primary transition-colors flex items-center justify-center gap-2"
                    >
                        <PaintBrush size={16} weight="light" /> Lihat Semua Finishing
                    </button>
                )}

                {isAdmin && (
                    <button
                        data-testid="scan-siap-kirim-btn"
                        onClick={() => navigate("/admin/siap-kirim")}
                        className="w-full border border-border py-3 text-sm text-muted-foreground hover:text-foreground hover:border-primary transition-colors flex items-center justify-center gap-2"
                    >
                        <Truck size={16} weight="light" /> Lihat Semua Siap Kirim
                    </button>
                )}

                {isAdmin && (
                    <button
                        data-testid="scan-terkirim-btn"
                        onClick={() => navigate("/admin/terkirim")}
                        className="w-full border border-border py-3 text-sm text-muted-foreground hover:text-foreground hover:border-primary transition-colors flex items-center justify-center gap-2"
                    >
                        <Package size={16} weight="light" /> Lihat Semua Terkirim
                    </button>
                )}

                {isAdmin && (
                    <>
                        <div className="grid grid-cols-2 gap-2" data-testid="dept-buttons">
                            <button
                                data-testid="dept-kantor-btn"
                                onClick={() => navigate("/admin/checklist/kantor")}
                                className="border border-border bg-white py-4 text-sm font-medium hover:border-primary hover:text-primary transition-colors flex flex-col items-center gap-2"
                            >
                                <Buildings size={22} weight="light" /> Kantor INERRE
                            </button>
                            <button
                                data-testid="dept-workshop-btn"
                                onClick={() => navigate("/admin/checklist/workshop")}
                                className="border border-border bg-white py-4 text-sm font-medium hover:border-primary hover:text-primary transition-colors flex flex-col items-center gap-2"
                            >
                                <Wrench size={22} weight="light" /> Workshop INERRE
                            </button>
                        </div>
                        <button
                            data-testid="scan-dashboard-link"
                            onClick={() => navigate("/admin/dashboard")}
                            className="w-full border border-border py-3 text-sm text-muted-foreground hover:text-foreground hover:border-primary transition-colors flex items-center justify-center gap-2"
                        >
                            <SquaresFour size={16} weight="light" /> Buka Dashboard Admin
                        </button>
                    </>
                )}
            </main>
        </div>
    );
}
